import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { OtherTokenReward } from 'types/customEnums';
import { ERoute, TanstackQueryName } from 'types/frontend';

export type Mission = {
  id: string;
  label: string;
  completed: boolean;
  route: ERoute | null;
  isPremium: boolean;
  description?: string;
};

export type OnboardingMissionsData = {
  missions: Mission[];
  completedCount: number;
  totalCount: number;
  allCompleted: boolean;
  hasAnyCompleted: boolean;
  isLoading: boolean;
};

export const MISSIONS_CONFIG = [
  {
    id: 'edit-profile',
    label: 'Personalize your profile',
    description: 'Upload a photo or update your name, bio, or links',
    route: ERoute.Profile as ERoute,
    isPremium: false,
  },
  {
    id: 'try-merlin',
    label: 'Send your first AI message',
    description: 'Chat with Merlin AI in any code editor',
    route: null as ERoute | null,
    isPremium: true,
  },
  {
    id: 'connect-discord',
    label: 'Join us on Discord',
    description: 'Connect your Discord and earn 1 BigToken',
    route: null as ERoute | null,
    isPremium: false,
  },
  {
    id: 'invite-friend',
    label: 'Invite a friend',
    description: 'Share your link and earn 1 BigToken per signup',
    route: null as ERoute | null,
    isPremium: false,
  },
];

/**
 * Returns true if the avatar URL is a custom upload (not the default GitHub avatar).
 * GitHub avatars come from avatars.githubusercontent.com.
 */
export const hasCustomAvatar = (avatarUrl: string): boolean =>
  avatarUrl !== '' && !avatarUrl.includes('githubusercontent.com');

/**
 * Detect whether the user has personalized any profile field.
 * Returns true if name differs from GitHub username, any optional field is filled,
 * or the user uploaded a custom avatar.
 */
export const hasEditedProfile = (context: {
  name: string;
  screenName: string;
  publicEmail: string;
  location: string;
  bio: string;
  websiteUrl: string;
  avatarUrl: string;
}): boolean =>
  context.name !== context.screenName ||
  context.publicEmail !== '' ||
  context.location !== '' ||
  context.bio !== '' ||
  context.websiteUrl !== '' ||
  hasCustomAvatar(context.avatarUrl);

/**
 * Build completion map from data + current user context.
 * Exported for testing.
 */
export const buildCompletionMap = (
  data: { hasAiUsage: boolean; hasReferral: boolean },
  context: {
    name: string;
    screenName: string;
    publicEmail: string;
    location: string;
    bio: string;
    websiteUrl: string;
    avatarUrl: string;
    hasDiscordRegistered: boolean;
    isLegacy: boolean;
  }
): Record<string, boolean> => ({
  'edit-profile': hasEditedProfile(context),
  'try-merlin': context.isLegacy ? false : data.hasAiUsage,
  'connect-discord': context.hasDiscordRegistered,
  'invite-friend': data.hasReferral,
});

/**
 * Map user's onboarding goal to the most relevant content route for AI mission.
 */
export const getAiMissionRoute = (goal?: string | null): ERoute => {
  switch (goal) {
    case 'build-product':
      return ERoute.Projects;
    case 'land-job':
      return ERoute.Challenges;
    case 'level-up':
      return ERoute.PracticeProblems;
    case 'explore':
    default:
      return ERoute.Challenges;
  }
};

export const useOnboardingMissions = (): OnboardingMissionsData => {
  const {
    id: userId,
    isLegacy,
    name,
    screenName,
    publicEmail,
    location,
    bio,
    websiteUrl,
    avatarUrl,
    hasDiscordRegistered,
  } = useCurrentUser();
  const supabase = useSupabaseTypedClient();

  const { data, isFetching } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetOnboardingMissions, userId],
    queryFn: async () => {
      const [aiUsage, referralCount] = await Promise.all([
        supabase
          .from('user_ai_usage')
          .select('base_tokens_used, extra_tokens_used')
          .eq('user_id', userId)
          .maybeSingle(),
        supabase
          .from('user_action')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('token_reward_id', OtherTokenReward.Referral),
      ]);

      const baseUsed = aiUsage.data?.base_tokens_used ?? 0;
      const extraUsed = aiUsage.data?.extra_tokens_used ?? 0;

      return {
        hasAiUsage: baseUsed > 0 || extraUsed > 0,
        hasReferral: (referralCount.count ?? 0) > 0,
      };
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  const completionMap = buildCompletionMap(
    { hasAiUsage: data?.hasAiUsage ?? false, hasReferral: data?.hasReferral ?? false },
    { name, screenName, publicEmail, location, bio, websiteUrl, avatarUrl, hasDiscordRegistered, isLegacy }
  );

  const missions: Mission[] = MISSIONS_CONFIG.map((config) => ({
    ...config,
    completed: completionMap[config.id],
  }));

  const completedCount = missions.filter((m) => m.completed).length;

  return {
    missions,
    completedCount,
    totalCount: missions.length,
    allCompleted: completedCount === missions.length,
    hasAnyCompleted: completedCount > 0,
    isLoading: isFetching && !data,
  };
};
